import { DEFAULT_PAGE_SIZE } from '@w7/ui'
import { ApiError, hasLiveApi } from '@/api/client'
import {
  searchCreditLimitLogs,
  toCreditLimitTransferResult,
} from '@/api/map/creditLimitLogs'
import { DEMO_CL_TRANSFERS } from './clTransfersDemo'
import type {
  CreditLimitTransferQuery,
  CreditLimitTransferResult,
  CreditLimitTransferRow,
} from './clTransfersTypes'

/**
 * Paginate a CL transfer list. Pure so unit tests pin Winner column behaviour
 * without mounting the page.
 */
export function paginateCreditLimitTransfers(
  rows: readonly CreditLimitTransferRow[],
  query: CreditLimitTransferQuery = {},
): CreditLimitTransferResult {
  const pageSize = Math.max(1, query.pageSize ?? DEFAULT_PAGE_SIZE)
  const total = rows.length
  const maxPage = Math.max(1, Math.ceil(total / pageSize) || 1)
  const page = Math.min(Math.max(1, query.page ?? 1), maxPage)
  const start = (page - 1) * pageSize
  return {
    rows: rows.slice(start, start + pageSize),
    total,
    page,
    pageSize,
  }
}

/** Demo / offline fixtures — never returned under a live Keycloak session. */
export function listDemoCreditLimitTransfers(
  query: CreditLimitTransferQuery = {},
): CreditLimitTransferResult {
  return paginateCreditLimitTransfers(DEMO_CL_TRANSFERS, query)
}

/** Live empty when hierarchy has no credit-limit log route / is unreachable. */
export function unavailableCreditLimitTransfers(
  query: CreditLimitTransferQuery = {},
  reason?: string,
): CreditLimitTransferResult {
  const pageSize = Math.max(1, query.pageSize ?? DEFAULT_PAGE_SIZE)
  return {
    rows: [],
    total: 0,
    page: 1,
    pageSize,
    unavailable: {
      code: 'no_credit_limit_log_source',
      reason:
        reason ??
        'Credit-limit transfer history needs an Agent-API credit-limit log. None is reachable, so this console will not invent balances, limits, or IPs.',
      trackedBy: 'backend-ui-logs:cl-transfers',
    },
  }
}

/**
 * Live Keycloak → hierarchy `searchCreditLimitLogs` (paise → display).
 * Demo / no live API → local fixtures only.
 */
export async function loadCreditLimitTransfers(
  query: CreditLimitTransferQuery = {},
  currency?: string | null,
): Promise<CreditLimitTransferResult> {
  if (!hasLiveApi()) return listDemoCreditLimitTransfers(query)

  try {
    const live = await searchCreditLimitLogs({
      page: query.page ?? 1,
      pageSize: query.pageSize ?? DEFAULT_PAGE_SIZE,
    })
    if (!live.value) {
      return unavailableCreditLimitTransfers(query, 'Credit-limit log returned no data.')
    }
    return toCreditLimitTransferResult(live, currency)
  } catch (err) {
    const status = err instanceof ApiError ? err.status : 0
    console.warn('[cl-transfers] hierarchy credit-limit log unavailable', err)
    return unavailableCreditLimitTransfers(
      query,
      status === 404
        ? 'Agent-API credit-limit log route is not deployed on this hierarchy yet.'
        : undefined,
    )
  }
}
